import DefaultBlock from "/scripts/default/block";
import gsap from "gsap";
import { watchPreferredMotion } from "/utils/helpers/watchPreferredMotion";
import CustomEase from "gsap/CustomEase";
import Physics2DPlugin from "gsap/Physics2DPlugin";
import MotionPathPlugin from "gsap/MotionPathPlugin";
import DrawSVGPlugin from "gsap/DrawSVGPlugin";

gsap.registerPlugin(
	CustomEase,
	Physics2DPlugin,
	MotionPathPlugin,
	DrawSVGPlugin
);

export default class PricingAnimation extends DefaultBlock {
	init() {
		this.DOM = {
			titleWordJoin: this.block.querySelector("#pricing-title-word-1"),
			titleWordClub: this.block.querySelector("#pricing-title-word-2"),
			titleWordGsap: this.block.querySelector("#pricing-title-word-3"),
			flairCircles: this.block.querySelector("#pricing-hero-circles"),
			flairStar: this.block.querySelector("#pricing-hero-star"),
			flairWindmill: this.block.querySelector("#pricing-flair-windmill"),
			flairSquiggle: this.block.querySelector("#pricing-flair-squiggle"),
			flairCutout: this.block.querySelector("#pricing-flair-cutout"),
			star: this.block.querySelector("#pricing-star"),
			starMotionPath: this.block.querySelector("#pricing-star-motion-path"),
			starPaths: this.block.querySelectorAll(".pricing-star-path"),
		};

		this.getStartPosition = this.getStartPosition.bind(this);

		this.createTimeline();
	}

	getStartPosition(el) {
		const {
			left: elLeft,
			top: elTop,
			width: elWidth,
			height: elHeight,
		} = el.getBoundingClientRect();

		const {
			left: textLeft,
			top: textTop,
			width: textWidth,
			height: textHeight,
		} = this.DOM.titleWordClub.getBoundingClientRect();

		return {
			x: textLeft + textWidth - (elLeft + elWidth / 2),
			y: textTop + textHeight / 2 - (elTop + elHeight / 2),
		};
	}

	createTimeline() {
		gsap.set([this.DOM.flairCircles, this.DOM.flairStar, this.DOM.star], {
			opacity: 0,
		});

		watchPreferredMotion(() => {
			const circlesStart = this.getStartPosition(this.DOM.flairCircles);
			const windmillStart = this.getStartPosition(this.DOM.flairWindmill);
			const squiggleStart = this.getStartPosition(this.DOM.flairSquiggle);
			const cutoutStart = this.getStartPosition(this.DOM.flairCutout);
			const textWidth = this.DOM.titleWordClub.offsetWidth;
			const textHeight = this.DOM.titleWordClub.offsetHeight;

			const hideElements = () => {
				const tl = gsap
					.timeline()
					.set(this.DOM.flairCircles, {
						scale: 0,
					})
					.set(this.DOM.star, { opacity: 0 })
					.set([this.DOM.flairCircles, this.DOM.flairStar], {
						opacity: 1,
					});

				return tl;
			};

			const circleExplosion = () => {
				const tl = gsap
					.timeline()
					.set(this.DOM.flairCircles, {
						x: circlesStart.x,
						y: circlesStart.y,
						rotateZ: 450,
					})
					.to(this.DOM.flairCircles, {
						motionPath: {
							path: [
								{
									x: circlesStart.x - textWidth * 1.5,
									y: circlesStart.y - textHeight * 2,
								},
								{ x: 0, y: 0 },
							],
						},
						scale: 1,
						duration: 1.2,
						rotation: "-=360",
						ease: "power2.out",
					});

				return tl;
			};

			const flairExplosion = () => {
				const tl = gsap
					.timeline()
					.fromTo(
						this.DOM.flairWindmill,
						{
							x: squiggleStart.x,
							y: squiggleStart.y,
							scale: 0,
							rotateZ: -180,
							opacity: 1,
						},
						{
							keyframes: [
								{
									scale: 1,
									duration: 0.2,
									ease: "power2.out",
								},
								{
									rotateZ: 0,
									duration: 0.8,
									ease: "power3.out",
									delay: -0.2,
								},
								{
									physics2D: {
										velocity: 700,
										angle: -50,
										gravity: 1700,
									},
									duration: 0.7,
									delay: -0.8,
								},
								{
									opacity: 0,
									duration: 0.2,
									ease: "power3.out",
									delay: -0.2,
								},
							],
						},
						0
					)
					.fromTo(
						this.DOM.flairCutout,
						{
							x: cutoutStart.x,
							y: cutoutStart.y,
							scale: 0,
							rotateZ: 270,
							opacity: 1,
						},
						{
							keyframes: [
								{
									scale: 1,
									duration: 0.2,
									ease: "power2.out",
								},
								{
									rotateZ: 0,
									duration: 0.6,
									ease: "power3.out",
									delay: -0.2,
								},
								{
									physics2D: {
										velocity: 900,
										angle: -80,
										gravity: 2000,
									},
									duration: 0.7,
									delay: -0.6,
								},
								{
									opacity: 0,
									duration: 0.2,
									ease: "power3.out",
									delay: -0.2,
								},
							],
						},
						0.1
					)
					.fromTo(
						this.DOM.flairSquiggle,
						{
							x: windmillStart.x,
							y: windmillStart.y,
							scale: 0,
							rotateZ: -90,
							opacity: 1,
						},
						{
							keyframes: [
								{
									scale: 1,
									duration: 0.2,
									ease: "power2.out",
								},
								{
									rotateZ: -120,
									duration: 0.6,
									ease: "power2.inOut",
									delay: -0.2,
								},
								{
									physics2D: {
										velocity: 900,
										angle: -103,
										gravity: 3000,
									},
									duration: 0.7,
									delay: -0.6,
								},
								{
									opacity: 0,
									duration: 0.2,
									ease: "power3.out",
									delay: -0.2,
								},
							],
						},
						0.2
					);

				return tl;
			};

			const starMove = () => {
				const tl = gsap.timeline();

				tl.set(this.DOM.star, { opacity: 1 })
					.to(this.DOM.star, {
						motionPath: {
							path: this.DOM.starMotionPath,
							autoRotate: 59,
							align: this.DOM.starMotionPath,
							alignOrigin: [0.5, 0.5],
						},
						duration: 1.2,
						ease: "power2.inOut",
					})
					.fromTo(
						this.DOM.starPaths,
						{
							drawSVG: 0,
						},
						{
							drawSVG: "100% 0%",
							ease: "power2.inOut",
							duration: 1.2,
						},
						0
					)
					.to(this.DOM.starPaths, {
						drawSVG: "100% 100%",
						ease: "power2.out",
						duration: 1,
					});

				return tl;
			};

			const masterTl = gsap.timeline({
				id: "pricing-hero",
				delay: 0.6,
			});

			masterTl
				.add(hideElements())
				.add(flairExplosion())
				.add(circleExplosion(), "<")
				.add(starMove(), 0.6)
				.timeScale(0.8);
		});
	}
}
